import {ChangeDetectionStrategy, Component, DOCUMENT, Inject, Input} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Breadcrumb, RoutingService} from '@spartacus/core';
import {ActiveFacetsComponent, FacetService} from '@spartacus/storefront';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {map, take} from 'rxjs/operators';

import {AimoFacet, AimoFacetValue, AimoProductSearchPage} from '../../../../model/product.model';
import {AimoAssortmentService, LabelSuffix} from '../../../../service/product/aimo-assortment.service';
import {AimoProductFacetService} from "../../../../service/product/aimo-product-facet.service";
import {AimoProductListComponentService} from "../../../../service/product/aimo-product-list-component.service";


@Component({
    selector: 'aimo-active-facets',
    templateUrl: './aimo-active-facets.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class AimoActiveFacetsComponent extends ActiveFacetsComponent {
    private _productSearchPage: AimoProductSearchPage;
    activeFacetList$ = new BehaviorSubject<Breadcrumb[]>([]);
    // eslint-disable-next-line
    clearUrlParams: { query: any };
    myAssortmentLabelSuffix$: Observable<LabelSuffix>;

    constructor(
        protected facetService: FacetService,
        protected route: ActivatedRoute,
        protected assortmentService: AimoAssortmentService,
        protected productFacetService: AimoProductFacetService,
        protected routingService: RoutingService,
        protected productListComponentService: AimoProductListComponentService,
        @Inject(DOCUMENT) protected document: Document
    ) {
        super(facetService);

        this.myAssortmentLabelSuffix$ = this.assortmentService.getMyAssortmentLabelSuffix();
        this.clearUrlParams = {
            query: this.route.snapshot.queryParams.query?.replace(
                this.route.snapshot.queryParams.query?.substring(
                    this.route.snapshot.queryParams.query?.indexOf(
                        ':',
                        this.route.snapshot.queryParams.query?.indexOf(':') + 1,
                    ),
                ),
                '',
            ), // clear everything after second :
        };
        this.setFacetList(false);
    }

    @Input()
    set productSearchPage(value: AimoProductSearchPage) {
        this._productSearchPage = value;
        this.setFacetList(this._productSearchPage.products.length === 0);
    }

    private setFacetList(noProducts: boolean) {
        this.facetList$.pipe(
            map((fl) => {
                const breadCrumbs = fl.facets
                    .filter(
                        (facet: AimoFacet) =>
                            facet.code !== 'category' &&
                            facet.code !== 'allCategories' &&
                            !AimoProductListComponentService.isDummyCategory(facet.code),
                    )
                    .flatMap((facet) =>
                        facet.values
                            .filter((value) => value.selected)
                            .flatMap(
                                (value) =>
                                    ({
                                        facetCode: (facet as AimoFacet).code,
                                        facetName: facet.name,
                                        facetValueCode: (value as AimoFacetValue).code,
                                        facetValueName: value.name,
                                        removeQuery: value.query,
                                    }) as Breadcrumb,
                            ),
                    );
                if (noProducts) {
                    return breadCrumbs.concat(
                        ...this._productSearchPage.breadcrumbs
                            ?.filter((af) =>
                                af.facetValueCode !== '0' &&
                                af.facetCode !== 'category' &&
                                !AimoProductListComponentService.isDummyCategory(af.facetCode))
                            .filter(
                                (af) =>
                                    breadCrumbs.find(
                                        (bc) =>
                                            af.facetValueCode === bc.facetValueCode && af.facetCode === bc.facetCode,
                                    ) === undefined,
                            ),
                    );
                }
                return breadCrumbs;
            }),
            take(1),
        ).subscribe(b =>
            this.activeFacetList$.next(b)
        );
    }

    isMyAssortmentSelected(): Observable<boolean> {
        return this.assortmentService.isOnlyMyAssortment();
    }

    isMyAssortmentSelectedOrActiveFacets(): Observable<boolean> {
        return combineLatest(this.assortmentService.isOnlyMyAssortment(), this.activeFacetList$).pipe(
            map(([data, activeFacets]) => {
                return data || activeFacets?.length > 0;
            }),
        );
    }

    removeMyAssortment(): void {
        this.assortmentService.setOnlyMyAssortment(false);
    }

    clickFacet(queryParams: { [p: string]: string }) {
        this.productFacetService.setScrollPosition(this.document.defaultView.window.pageYOffset);
        this.routingService.go(
            [],
            {
                queryParams: queryParams,
                relativeTo: this.route,
                fragment: 'search-result-header'
            }
        );
    }

}
